import React from "react";
import { FiX, FiThumbsUp, FiAlertCircle } from "react-icons/fi";
import { PostLikeResponseDto } from "../../types/types";

interface FeedsModalsProps {
  showLikesModal: number | null;
  loadingLikes: boolean;
  likesData: PostLikeResponseDto[];
  deleteModalPostId: number | null;
  reportModalPostId: number | null;
  reportReason: string;
  onCloseLikesModal: () => void;
  onCloseDeleteModal: () => void;
  onConfirmDelete: (postId: number) => void;
  onCloseReportModal: () => void;
  onSetReportReason: React.Dispatch<React.SetStateAction<string>>;
  onConfirmReport: (postId: number) => void;
  onViewProfile: (profileId: string | number) => void;
  t: (key: string, fallback?: string) => string;
}

const FeedsModals: React.FC<FeedsModalsProps> = ({
  showLikesModal,
  loadingLikes,
  likesData,
  deleteModalPostId,
  reportModalPostId,
  reportReason,
  onCloseLikesModal,
  onCloseDeleteModal,
  onConfirmDelete,
  onCloseReportModal,
  onSetReportReason,
  onConfirmReport,
  onViewProfile,
  t,
}) => {
  return (
    <>
      {showLikesModal !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-labelledby="likes-modal-title"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 id="likes-modal-title" className="text-lg font-semibold text-gray-900">
                {t("peopleWhoLiked")}
              </h3>
              <button
                onClick={onCloseLikesModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
                aria-label={t("close", "Close")}
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loadingLikes ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : likesData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <FiThumbsUp className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">{t("noLikesYet")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {likesData.map((like) => (
                    <button
                      type="button"
                      key={like.id}
                      onClick={() => onViewProfile(like.profile.id)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left outline-none focus:outline-none focus:ring-0"
                      aria-label={`${t("viewProfile", "View profile")} ${like.profile.firstName} ${like.profile.lastName}`}
                    >
                      {like.profile.profileImage ? (
                        <img
                          src={like.profile.profileImage}
                          alt={`${like.profile.firstName} ${like.profile.lastName}'s profile`}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white font-semibold uppercase flex-shrink-0">
                          {`${like.profile.firstName?.[0] || ""}${like.profile.lastName?.[0] || ""}`}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-start text-sm text-gray-900 truncate">
                          {like.profile.firstName} {like.profile.lastName}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalPostId !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-labelledby="delete-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl p-4 w-full max-w-xs">
            <h3 id="delete-modal-title" className="text-sm font-medium text-gray-900 mb-4 text-center">
              {t("deleteConfirmationPost")}
            </h3>
            <div className="flex justify-center gap-3">
              <button
                onClick={onCloseDeleteModal}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
                aria-label={t("cancel")}
              >
                {t("cancel")}
              </button>
              <button
                onClick={() => onConfirmDelete(deleteModalPostId)}
                className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
                aria-label={t("delete")}
              >
                {t("delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportModalPostId !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-labelledby="report-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FiAlertCircle className="w-5 h-5 text-red-600" />
                <h3 id="report-modal-title" className="text-lg font-semibold text-gray-900">
                  {t("reportPost")}
                </h3>
              </div>
              <button
                onClick={onCloseReportModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
                aria-label={t("close")}
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                {t("reportPostDescription")}
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => onSetReportReason(e.target.value)}
                placeholder={t("reportReasonPlaceholder")}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent resize-none"
                rows={4}
                aria-label={t("reportReasonPlaceholder")}
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={onCloseReportModal}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm font-medium"
                aria-label={t("cancel")}
              >
                {t("cancel")}
              </button>
              <button
                onClick={() => onConfirmReport(reportModalPostId)}
                disabled={!reportReason.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${reportReason.trim()
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                aria-label={t("submitReport")}
              >
                {t("submitReport")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedsModals;
